import React, { useState, useCallback, useMemo, useEffect } from "react"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  useCreateAccessoriesList,
  useEditAccessoriesList,
} from "hooks/useAccessoriesLists"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"

const AccessoriesAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccessoriesList()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccessoriesList()

  // -------------------- Fetch unit options --------------------
  const { raw: rawOptions, isLoading: optionLoading } =
    useFilteredVehicleOptions()

  const [createdUnits, setCreatedUnits] = useState([])

  useEffect(() => {
    if (!modal) setCreatedUnits([])
  }, [modal])

  const unitOptions = useMemo(() => {
    const rawUnits = rawOptions?.units ?? []
    const combined = [...rawUnits, ...createdUnits]
    return combined.map(u => ({ value: u.id?.toString(), label: u.name }))
  }, [rawOptions?.units, createdUnits])

  const handleCreateUnit = useCallback(inputValue => {
    const tempId = `new_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`
    setCreatedUnits(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  const getUnitIdValue = useCallback(value => {
    if (value.startsWith("new_")) {
      const created = createdUnits.find(u => u.id === value)
      return created ? created.name : value
    }
    return value
  }, [createdUnits])

  // -------------------- Validation --------------------
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter accessories name")
      .max(255, "Name cannot exceed 255 characters"),
    description: Yup.string().max(1000, "Description cannot exceed 1000 characters"),
    unit_id: Yup.string().required("Select a unit"),
  })

  // -------------------- Formik --------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      description: editedData?.description || "",
      unit_id: editedData?.unit_id?.toString() || "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          unit_id: getUnitIdValue(values.unit_id),
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ?? `Accessories ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save accessories.")
      }
    },
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Accessories"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  // -------------------- Render --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Accessories" : "Add Accessories"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {/* Name */}
        <div className="mb-3">
          <Label htmlFor="name">Name <span className="text-danger">*</span></Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        {/* Description */}
        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.description && !!formik.errors.description}
          />
          {formik.touched.description && formik.errors.description && (
            <FormFeedback type="invalid">{formik.errors.description}</FormFeedback>
          )}
        </div>

        {/* Unit */}
        <FormikCreatableSelect
          name="unit_id"
          label="Unit"
          formik={formik}
          options={unitOptions}
          placeholder="Select unit"
          loading={optionLoading}
          required
          onCreateOption={handleCreateUnit}
        />

        {/* Submit */}
        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={editLoading || createLoading}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccessoriesAddEdit
